import styled, { useTheme } from 'styled-components';
import { FaHeart, FaCheckCircle } from 'react-icons/fa'; // 아이콘 추가
import { useNavigate } from 'react-router-dom';
import { addCart, getCartList } from '../api/services/cartService';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from './ui';
import { setUserCartList } from '../redux/userCart';
import { addFavorites } from '../api/services/favoritesService';
import { useEffect, useState } from 'react';
import { useGuestCheck } from '../hooks/useGuestCheck';
import { navigateToLogin } from '../utils';
import { createRoot } from 'react-dom/client';

const ProductCardContainer = styled.div`
  display: flex;
  text-decoration: none;
  color: inherit;
  width: 12vw;
  border-radius: 5px;
  padding: 20px 30px;
  text-align: center;
  flex-direction: column;
  position: relative;
  transition: box-shadow 0.3s ease-in-out;

  cursor: pointer;

  &:hover {
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  }

  /* Tablet responsive */
  @media (max-width: 768px) {
    width: 100%;
    padding: 15px 10px;
    box-sizing: border-box;
  }

  /* Mobile responsive */
  @media (max-width: 480px) {
    width: 100%;
    min-width: 35vw;
    padding: 10px 8px;
    box-sizing: border-box;
  }

  /* Small mobile responsive */
  @media (max-width: 400px) {
    width: 100%;
    padding: 8px 6px;
    box-sizing: border-box;
  }

  /* Touch device fallback */
  @media (hover: none) and (pointer: coarse) {
    &:active {
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
    }
  }
`;

const ProductImagesWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 12vw;
  /* border: 1px solid #ddd; */
  object-fit: contain;
  background-color: #fff;

  /* Tablet responsive */
  @media (max-width: 768px) {
    height: 120px;
  }

  /* Mobile responsive */
  @media (max-width: 480px) {
    height: 100px;
  }

  /* Small mobile responsive */
  @media (max-width: 400px) {
    height: 80px;
  }
`;

const HoverButtons = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0px;
  left: 0px;
  display: flex;
  gap: 8px;
  opacity: 0;
  visibility: hidden;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.3);
  transition:
    opacity 0.3s ease-in-out,
    visibility 0.3s ease-in-out;

  ${ProductCardContainer}:hover & {
    opacity: 1;
    visibility: visible;
  }

  /* Hide on mobile devices */
  @media (max-width: 768px) {
    display: none;
  }
`;

const ProductName = styled.h3`
  font-size: 0.95rem;
  font-weight: 400;
  margin-top: 10px;
  display: block;
  overflow: hidden;
  white-space: normal;
  word-break: normal;
  overflow-wrap: normal;
  height: 2.4em;
  line-height: 1.2em;

  /* Tablet responsive */
  @media (max-width: 768px) {
    font-size: 0.9rem;
    height: 2.4em;
    line-height: 1.2em;
  }

  /* Mobile responsive */
  @media (max-width: 480px) {
    font-size: 0.8rem;
    height: 2.2em;
    line-height: 1.1em;
  }
`;

const ProductDescr = styled.div`
  font-size: 0.9rem;
  margin: 10px 0 5px;
  height: 1rem;
  line-height: 0.9rem;
  color: #1e1e1e;
  display: -webkit-box;
  -webkit-line-clamp: 2; /* ✅ 2줄에서 잘라냄 */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  /* Tablet responsive */
  @media (max-width: 768px) {
    font-size: 0.8rem;
    height: 1.7rem;
    line-height: 0.85rem;
  }

  /* Mobile responsive */
  @media (max-width: 480px) {
    font-size: 0.7rem;
    height: 1.5rem;
    line-height: 0.75rem;
  }
`;

const ProductCode = styled.span`
  display: block;
  font-size: 0.85rem;
  font-weight: 700;
  color: #1e1e1e;
  margin-bottom: 2px;

  /* Tablet responsive */
  @media (max-width: 768px) {
    font-size: 0.75rem;
    margin-bottom: 4px;
  }

  /* Mobile responsive */
  @media (max-width: 480px) {
    font-size: 0.65rem;
    margin-bottom: 3px;
  }
`;

const StockInfo = styled.div`
  font-size: 0.85rem;
  color: ${(props) =>
    props.inStock
      ? props.theme.colors.primaryGreen
      : props.theme.colors.primaryRed};
  margin-bottom: 6px;

  /* Tablet responsive */
  @media (max-width: 768px) {
    font-size: 0.75rem;
    margin-bottom: 4px;
  }

  /* Mobile responsive */
  @media (max-width: 480px) {
    font-size: 0.65rem;
    margin-bottom: 3px;
  }
`;

const PriceInfo = styled.div`
  font-size: 0.85rem;
  color: #1e1e1e;
  margin-bottom: 4px;

  /* Tablet responsive */
  @media (max-width: 768px) {
    font-size: 0.75rem;
    margin-bottom: 3px;
  }

  /* Mobile responsive */
  @media (max-width: 480px) {
    font-size: 0.65rem;
    margin-bottom: 2px;
  }
`;

const UnitPrice = styled.div`
  font-size: 15px;
  font-weight: bold;
  margin-bottom: 12px;

  /* Tablet responsive */
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-top: 8px;
    margin-bottom: 8px;
  }

  /* Mobile responsive */
  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-top: 8px;
    margin-bottom: 6px;
  }
`;

const AddToCartButton = styled.button`
  background-color: ${(props) =>
    props.$accentColor || props.theme.colors.primaryBlue};
  color: #fff;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 10px;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #0d1c66;
    color: #ffffff;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    opacity: 0.6;
    &:hover {
      background-color: #cccccc;
    }
  }

  /* Tablet responsive */
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 8px;
  }

  /* Mobile responsive */
  @media (max-width: 480px) {
    font-size: 0.7rem;
    padding: 6px;
  }
`;

const CartControls = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  align-items: stretch;
  justify-content: center;

  > ${AddToCartButton} {
    flex: 0 0 auto;
    min-height: 40px;
    padding: 0 14px;
    white-space: nowrap;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 4px;
  }
`;

const QuantitySelector = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  height: 38px;
  border: 1px solid #d7d9e3;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 2px rgba(25, 30, 60, 0.06);

  @media (max-width: 480px) {
    justify-content: space-between;
  }
`;

const QuantityButton = styled.button`
  width: 34px;
  height: 100%;
  border: 0;
  background: #fff;
  color: ${(props) => props.theme.colors.primaryBlue};
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover:not(:disabled) {
    background: #f0f1fa;
  }

  &:disabled {
    color: #c4c6cf;
    background: #fafafa;
    cursor: not-allowed;
  }
`;

const QuantityValue = styled.span`
  min-width: 38px;
  padding: 0 6px;
  font-size: 0.9rem;
  font-weight: 700;
  color: #202335;
  text-align: center;
  font-variant-numeric: tabular-nums;
`;

const ActionButtons = styled.div`
  display: none;
  gap: 8px;
  align-items: center;
  justify-content: center;
  margin-top: 8px;

  /* Show only on mobile devices */
  @media (max-width: 768px) {
    display: flex;
    gap: 6px;
    margin-top: 6px;
  }

  /* Mobile responsive */
  @media (max-width: 480px) {
    gap: 4px;
    margin-top: 4px;
  }
`;

const IconButton = styled.button`
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 1);
  }

  /* Tablet responsive */
  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
  }

  /* Mobile responsive */
  @media (max-width: 480px) {
    width: 24px;
    height: 24px;
  }
`;

// Parent/Child Item: 그룹(여러 size/diameter)을 가진 카드에 표시하는 배지
const OptionBadge = styled.div`
  position: absolute;
  top: 6px;
  left: 6px;
  background: ${(props) => props.theme.colors.primaryBlue};
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 12px;
  z-index: 1;
`;

export default function ProductCard({
  data,
  style,
  enableQuantity = false,
  accentColor,
  accentHoverColor,
}) {
  const userInfo = useSelector((state) => state.userInfo);
  const userCart = useSelector((state) => state.userCart);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const theme = useTheme();
  const { isGuestUser } = useGuestCheck();

  const {
    idStyle,
    webTitle,
    websitename,
    filepath1,
    avaQty,
    price,
    retailPrice,
    salePrice,
    image,
    color,
    qtyPerUOM,
    qtyPerCTN,
    childCount,
    groupName,
  } = data;

  // 그룹 대표 카드는 그룹명을, 그룹 멤버 카드는 원래 상품명을 표시
  const displayName =
    data.isGroupCard && childCount > 1 && groupName
      ? groupName
      : websitename || webTitle;

  const [useFallback, setUseFallback] = useState(false);
  const packQuantity = Math.max(1, Number(qtyPerUOM) || 1);
  const [quantity, setQuantity] = useState(packQuantity);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const cartQuantity = (userCart.cart_list || [])
    .filter((item) => item.idStyle === idStyle)
    .reduce((sum, item) => sum + (Number(item.totQty) || 0), 0);
  const remainingQuantity = Math.max(0, Number(avaQty || 0) - cartQuantity);
  const canAddSelectedQuantity =
    !enableQuantity || quantity <= remainingQuantity;

  useEffect(() => {
    const maxPackQuantity =
      Math.floor(remainingQuantity / packQuantity) * packQuantity;

    if (maxPackQuantity >= packQuantity && quantity > maxPackQuantity) {
      setQuantity(maxPackQuantity);
    } else if (quantity < packQuantity || quantity % packQuantity !== 0) {
      setQuantity(packQuantity);
    }
  }, [idStyle, packQuantity, quantity, remainingQuantity]);

  // Dynamic URL based on error state and fallback
  const imgUrl = image
    ? image.replace(' copy', '').replace(' SAP', '')
    : useFallback || !filepath1
      ? `${process.env.REACT_APP_FILE_UPLOAD_URL}/style/${idStyle.replace(/\//g, '-')}.jpg`
      : `${process.env.REACT_APP_FILE_UPLOAD_URL}${filepath1.replace(' copy', '').replace(' SAP', '')}`;

  const handleImageError = () => {
    if (!useFallback && filepath1) {
      // First failure: try the fallback idStyle URL
      setUseFallback(true);
    }
    // If both URLs fail, the card will still show with a broken image icon
  };

  // Always render the card, even if images fail to load
  // The card will show with a broken image icon

  const inStock = avaQty > 0;

  const getDbCartList = async () => {
    // Cart requires authentication; skip if there is no token so we don't
    // trigger an unhandled 401 when logged-out or session expired.
    if (!localStorage.getItem('token')) return;
    if (userInfo.zipcode) {
      try {
        const result = await getCartList(
          userInfo.idCustomer,
          userInfo.zipcode,
          userInfo.idStore,
        );
        dispatch(setUserCartList({ cart_list: result.cartList }));
      } catch (e) {
        // Cart fetch failed (e.g. not authenticated) — leave cart empty.
      }
    }
  };

  const showCartAlert = (message) => {
    const container = document.createElement('div');
    document.body.appendChild(container);

    const root = createRoot(container);

    const close = () => {
      root.unmount();
      container.remove();
    };

    root.render(
      <div
        onClick={close}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(211, 211, 211, 0.5)', // #D3D3D3
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'relative',
            width: '400px',
            padding: '2rem',
            background: '#fff',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,.2)',
          }}
        >
          <button
            onClick={close}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '20px',
            }}
          >
            ×
          </button>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              textAlign: 'left',
            }}
          >
            {/* primaryGreen */}
            <FaCheckCircle color='rgb(0, 153, 0)' size={32} />
            <span dangerouslySetInnerHTML={{ __html: message }} />
          </div>

          {!enableQuantity && (
            <button
              onClick={() => {
                window.location.href = '/cart';
              }}
              style={{
                marginTop: '20px',
                width: '100%',
                background: '#2b388f',
                color: '#fff',
                border: 'none',
                padding: '10px',
                cursor: 'pointer',
              }}
            >
              View Cart
            </button>
          )}
        </div>
      </div>,
    );

    setTimeout(close, 3000);
  };

  const handleAddToCart = async (e) => {
    e.stopPropagation();

    if (!inStock || !canAddSelectedQuantity || isAddingToCart) {
      return;
    }

    setIsAddingToCart(true);
    let result;
    try {
      result = await addCart(
        userInfo.idCustomer,
        idStyle,
        color,
        enableQuantity ? quantity : packQuantity,
      );
    } finally {
      setIsAddingToCart(false);
    }

    const decodeHtml = (text) => {
      const txt = document.createElement('textarea');
      txt.innerHTML = text;
      return txt.value;
    };

    if (result.is_success) {
      getDbCartList();
      showCartAlert(
        `${decodeHtml(websitename || webTitle)}<br />added to cart!`,
      );
    } else {
      alert(result.message || `Error while add to cart`);
    }
  };

  const showFavoritesAlert = (message) => {
    const container = document.createElement('div');
    document.body.appendChild(container);

    const root = createRoot(container);

    const close = () => {
      root.unmount();
      container.remove();
    };

    root.render(
      <div
        onClick={close}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(211, 211, 211, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'relative',
            width: '400px',
            padding: '2rem',
            background: '#fff',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,.2)',
          }}
        >
          <button
            onClick={close}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '20px',
            }}
          >
            ×
          </button>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              textAlign: 'left',
            }}
          >
            <FaCheckCircle color='rgb(0, 153, 0)' size={32} />
            <span dangerouslySetInnerHTML={{ __html: message }} />
          </div>

          <button
            onClick={() => {
              window.location.href = '/Favorites';
            }}
            style={{
              marginTop: '20px',
              width: '100%',
              background: '#2b388f',
              color: '#fff',
              border: 'none',
              padding: '10px',
              cursor: 'pointer',
            }}
          >
            View Favorites
          </button>
        </div>
      </div>,
    );

    setTimeout(close, 3000);
  };

  const handleFavorites = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (userInfo.userId == 0) {
      navigateToLogin(navigate, `/product?id=${idStyle}`);
      return;
    }

    const result = await addFavorites(
      userInfo.idCustomer,
      idStyle,
      color,
      qtyPerUOM,
      userInfo.idStore,
    );
    if (result.is_success) {
      showFavoritesAlert(`Added to favorites<br />${websitename || webTitle}`);
    } else {
      alert(`Error while add to Favorites`);
    }
  };

  return (
    <ProductCardContainer
      onClick={() => {
        if (userInfo.userId != 0) {
          navigate(`/product?id=${idStyle}`);
        } else {
          navigateToLogin(navigate, `/product?id=${idStyle}`);
        }
        window.scrollTo(0, 0);
      }}
      style={style}
    >
      <ProductImagesWrapper>
        {childCount > 1 && data.isGroupCard && (
          <OptionBadge>{`${childCount} sizes`}</OptionBadge>
        )}
        <ProductImage
          src={imgUrl}
          alt={`${websitename || webTitle}`}
          onError={handleImageError}
        />
        <HoverButtons>
          <IconButton onClick={handleFavorites}>
            <FaHeart size={16} color={theme.colors.primaryRed} />
          </IconButton>
        </HoverButtons>
      </ProductImagesWrapper>

      <ActionButtons>
        <IconButton onClick={handleFavorites}>
          <FaHeart size={16} color={theme.colors.primaryRed} />
        </IconButton>
      </ActionButtons>

      <ProductName>{displayName}</ProductName>
      <ProductCode>{idStyle}</ProductCode>
      <ProductDescr>{`Pack : ${qtyPerUOM} / Carton : ${qtyPerCTN}`}</ProductDescr>
      <StockInfo inStock={inStock}>
        {inStock ? 'In Stock' : 'Out of stock'}
      </StockInfo>

      <PriceInfo>
        {userInfo.userId != 0 && retailPrice != null
          ? `Retail Price (SRP) : $${Number(retailPrice).toFixed(2)}`
          : ''}
      </PriceInfo>
      <UnitPrice>
        {userInfo.userId != 0 &&
        !isGuestUser &&
        price != null &&
        salePrice != null
          ? `Your Price: $${(Number(price) - Number(salePrice)).toFixed(2)}`
          : ''}
      </UnitPrice>
      {userInfo.userId != 0 ? (
        isGuestUser ? (
          <AddToCartButton disabled style={{ opacity: 0.5 }}>
            GUEST - CART DISABLED
          </AddToCartButton>
        ) : (
          <CartControls onClick={(e) => e.stopPropagation()}>
            {enableQuantity && (
              <QuantitySelector aria-label='Purchase quantity'>
                <QuantityButton
                  type='button'
                  aria-label='Decrease quantity'
                  disabled={quantity <= packQuantity}
                  onClick={() => setQuantity(quantity - packQuantity)}
                >
                  −
                </QuantityButton>
                <QuantityValue>{quantity}</QuantityValue>
                <QuantityButton
                  type='button'
                  aria-label='Increase quantity'
                  disabled={quantity + packQuantity > remainingQuantity}
                  onClick={() => setQuantity(quantity + packQuantity)}
                >
                  +
                </QuantityButton>
              </QuantitySelector>
            )}
            <AddToCartButton
              $accentColor={accentColor}
              $accentHoverColor={accentHoverColor}
              onClick={handleAddToCart}
              disabled={!inStock || !canAddSelectedQuantity || isAddingToCart}
            >
              {isAddingToCart ? 'ADDING...' : 'ADD TO CART'}
            </AddToCartButton>
          </CartControls>
        )
      ) : (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            navigateToLogin(navigate, `/product?id=${idStyle}`);
            window.scrollTo(0, 0);
          }}
        >
          Login to see price
        </Button>
      )}
    </ProductCardContainer>
  );
}
